import { invariant } from '../util/invariant';
import type { Fraction } from '../util/fractionType';
import type { Logger } from '../types/logger';
import type { FieldOrder, PixelFormat, Resolution } from './types';
import { pixelFormatToBitsPerPixel } from './types';
import { ReadonlyBuffer } from '../util/readonlyBuffer';
import { MultiGuard } from '../async/async';
import { approximateFractionValue, compareFractions, divideFractions, formatFractionAsExpression, frac, multiplyFractions, validateFraction } from '../util/integerMath';
import { makeValidationFunction, validateNumberProperty, validateObjectOrNullProperty, validateObjectProperty } from '../util/validation';
import { validateFieldOrder, validatePixelFormat, validateResolution } from './validation';

export interface RawVideoInfo {
  resolution: Resolution,
  pixelFormat: PixelFormat,
  fieldOrder: FieldOrder,
}

export interface RawVideoFrame extends RawVideoInfo {
  buffer: ReadonlyBuffer,
}

export interface LogFriendlyRawVideoFrame extends RawVideoInfo {
  byteCount: number,
}

export function calculateRawVideoFrameBufferSize(
  width: number,
  height: number,
  pixelFormat: PixelFormat,
): number {
  const bitsPerPixel = pixelFormatToBitsPerPixel(pixelFormat);
  return (width * height * bitsPerPixel) / 8;
}

export function validateRawVideoFrame(videoFrame: RawVideoFrame | null) {
  if (videoFrame === null) {
    return;
  }
  const { resolution, pixelFormat, buffer } = videoFrame;
  const { width, height, } = resolution;
  const videoFrameSize = calculateRawVideoFrameBufferSize(width, height, pixelFormat);
  invariant(
    videoFrameSize == buffer.length,
    () => `Incorrect video frame buffer size. Expected ${videoFrameSize} bytes but got ${buffer.length} bytes. (resolution=${width}x${height}, pixelFormat=${pixelFormat}`
  );
}

export type RawAudioFormat = 'pcm16' | 'float32';

export interface RawAudioInfo {
  sampleRate: number,
  channelCount: number,
  format: RawAudioFormat,
}

export interface RawAudioFrame extends RawAudioInfo {
  sampleOffset: number,
  sampleCount: number,
  buffer: ReadonlyBuffer,
}

export interface LogFriendlyRawAudioFrame extends RawAudioInfo {
  sampleOffset: number,
  sampleCount: number,
  byteCount: number,
}

export interface LogFriendlyRawDataFrame extends RawDataInfo {
  numberOfBytes: number,
}

export function bytesPerSampleFromRawAudioFormat(format: RawAudioFormat): number {
  switch (format) {
    case 'pcm16': return 2;
    case 'float32': return 4;
  }
}

export function calculateRawAudioFrameBufferSize(rawAudioInfo: RawAudioInfo, sampleCount: number): number {
  const bytesPerSample = bytesPerSampleFromRawAudioFormat(rawAudioInfo.format);
  const audioFrameSize = sampleCount * rawAudioInfo.channelCount * bytesPerSample;
  return audioFrameSize;
}

export function calculateSampleOffsetForFrameNumber(sampleRate: number, frameRate: Fraction, frameNumber: number): number {
  const sampleRateFraction = frac(sampleRate, 1);
  const frameNumberFraction = frac(frameNumber, 1);
  const seconds = divideFractions(frameNumberFraction, frameRate);
  const sampleOffsetFraction = multiplyFractions(seconds, sampleRateFraction);
  return Math.floor(approximateFractionValue(sampleOffsetFraction));
}

export function calculateSampleCountForFrameNumber(sampleRate: number, frameRate: Fraction, frameNumber: number): number {
  const offset0 = calculateSampleOffsetForFrameNumber(sampleRate, frameRate, frameNumber);
  const offset1 = calculateSampleOffsetForFrameNumber(sampleRate, frameRate, frameNumber + 1);
  return offset1 - offset0;
}

export function validateRawAudioFrame(audioFrame: RawAudioFrame | null) {
  if (audioFrame === null) {
    return true;
  }
  const { sampleCount, channelCount, format, buffer } = audioFrame;
  const audioFrameSize = calculateRawAudioFrameBufferSize(audioFrame, sampleCount);
  invariant(
    audioFrameSize === buffer.length,
    () => `Incorrect audio frame buffer size. Expected ${audioFrameSize} bytes but got ${buffer.length} bytes. (sampleCount=${sampleCount}, channelCount=${channelCount}, format=${format})`
  );
}

export type RawDataCodec = 'smpte_436m';

export interface RawDataInfo {
  codec: RawDataCodec,
}

export interface RawInfo {
  frameRate: Fraction,
  video: RawVideoInfo | null,
  audio: RawAudioInfo | null,
  data: RawDataInfo | null,
}

export interface RawDataFrame extends RawDataInfo {
  bytes: ReadonlyBuffer,
}

export interface RawFrame extends RawInfo {
  frameNumber: number,
  video: RawVideoFrame | null,
  audio: RawAudioFrame | null,
  data: RawDataFrame | null,
}

export interface LogFriendlyRawFrame extends RawInfo {
  frameNumber: number,
  video: LogFriendlyRawVideoFrame | null,
  audio: LogFriendlyRawAudioFrame | null,
}

export function cleanRawVideoInfo(video: RawVideoFrame | null): RawVideoInfo | null {
  if (video === null) {
    return null;
  }
  return {
    resolution: video.resolution,
    pixelFormat: video.pixelFormat,
    fieldOrder: video.fieldOrder,
  };
}

export function cleanRawAudioInfo(audio: RawAudioFrame | null): RawAudioInfo | null {
  if (audio === null) {
    return null;
  }
  return {
    sampleRate: audio.sampleRate,
    channelCount: audio.channelCount,
    format: audio.format,
  };
}

export function cleanRawDataInfo(data: RawDataFrame | null): RawDataInfo | null {
  if (data === null) {
    return null;
  }

  return { codec: data.codec };
}

export function cleanRawInfo(frame: RawFrame): RawInfo {
  return {
    frameRate: frame.frameRate,
    video: cleanRawVideoInfo(frame.video),
    audio: cleanRawAudioInfo(frame.audio),
    data: cleanRawDataInfo(frame.data),
  }
}

export function logFriendlyVideoFrame(videoFrame: RawVideoFrame | null): LogFriendlyRawVideoFrame | null {
  if (videoFrame === null) {
    return null;
  }
  return {
    resolution: videoFrame.resolution,
    pixelFormat: videoFrame.pixelFormat,
    fieldOrder: videoFrame.fieldOrder,
    byteCount: videoFrame.buffer.length,
  }
}

export function logFriendlyAudioFrame(audioFrame: RawAudioFrame | null): LogFriendlyRawAudioFrame | null {
  if (audioFrame === null) {
    return null;
  }

  return {
    sampleRate: audioFrame.sampleRate,
    sampleCount: audioFrame.sampleCount,
    sampleOffset: audioFrame.sampleOffset,
    channelCount: audioFrame.sampleCount,
    format: audioFrame.format,
    byteCount: audioFrame.buffer.length,
  };
}

export function logFriendlyDataFrame(data: RawDataFrame | null): LogFriendlyRawDataFrame | null {
  if (data === null) {
    return null;
  }
  return {
    codec: data.codec,
    numberOfBytes: data.bytes.length,
  };
}

export function logFriendlyFrame(frame: RawFrame): LogFriendlyRawFrame {
  return {
    frameNumber: frame.frameNumber,
    video: logFriendlyVideoFrame(frame.video),
    audio: logFriendlyAudioFrame(frame.audio),
    data: logFriendlyDataFrame(frame.data),
    frameRate: frame.frameRate,
  }
}

export function validateRawFrame(nutFrame: RawFrame) {
  validateRawVideoFrame(nutFrame.video);
  validateRawAudioFrame(nutFrame.audio);

  invariant(
    nutFrame.frameRate.denominator > 0 && Number.isInteger(nutFrame.frameRate.denominator),
    'Frame rate denominator must be a positive integer'
  );
  invariant(
    nutFrame.frameRate.numerator > 0 && Number.isInteger(nutFrame.frameRate.numerator),
    'Frame rate numerator must be a positive integer'
  );
}


export function rawVideoFrameIncompatibilityReason(a: RawVideoInfo | null, b: RawVideoInfo | null): null | string {
  if (a === null || b === null) {
    if ((a === null) && (b !== null)) {
      return `changed from not having video to having video`;
    }
    if ((b !== null) && (b === null)) {
      return `changed from having video to not having video`;
    }
    return null;
  }
  if (a.pixelFormat !== b.pixelFormat) {
    return `pixelFormat mismatch: ${a.pixelFormat} != ${b.pixelFormat}`;
  }

  if ((a.resolution.width !== b.resolution.width) || (a.resolution.height !== b.resolution.height)) {
    return `resolution mismatch: ${a.resolution.width}x${a.resolution.height} != ${b.resolution.width}x${b.resolution.height}`;
  }
  return null;
}

export function rawAudioFrameIncompatibilityReason(a: RawAudioInfo | null, b: RawAudioInfo | null): null | string {
  if (a === null || b === null) {
    if ((a === null) && (b !== null)) {
      return `changed from not having audio to having audio`;
    }
    if ((b !== null) && (b === null)) {
      return `changed from having audio to not having audio`;
    }
    return null;
  }

  if (a.format !== b.format) {
    return `format mismatch: ${a.format} != ${b.format}`;
  }
  if (a.sampleRate !== b.sampleRate) {
    return `sampleRate mismatch: ${a.sampleRate} != ${b.sampleRate}`;
  }

  if (a.channelCount !== b.channelCount) {
    return `channelCount mismatch: ${a.channelCount} != ${b.channelCount}`;
  }
  return null;
}

export function rawFrameIncompatibilityReason(a: RawInfo, b: RawInfo): null | string {
  const videoReason = rawVideoFrameIncompatibilityReason(a.video, b.video);
  if (videoReason !== null) {
    return `Video mismatch: ${videoReason}`;
  }
  const audioReason = rawAudioFrameIncompatibilityReason(a.audio, b.audio);
  if (audioReason !== null) {
    return `Audio mismatch: ${audioReason}`;
  }
  if (compareFractions(a.frameRate, b.frameRate) !== 0) {
    return `Frame rate changed from ${formatFractionAsExpression(a.frameRate)} to ${formatFractionAsExpression(b.frameRate)}`;
  }
  return null;
}

export function assertRawFramesCompatible(a: RawInfo, b: RawInfo) {
  const reason = rawFrameIncompatibilityReason(a, b);
  if (reason !== null) {
    throw new Error(`Raw frames compatibility assertion failed: ${reason}`);
  }
}

/**
 *  This utility will rewrite the raw frames so that the output frameNumber
 *  starts on frame zero and increments by 1 for each frame processed. It will
 *  also ensure that the audio sample offset starts at zero and increments with
 *  the number of samples included in each frame.
 */
export class RawFrameStreamOffsetRewriter {
  private currentFrameNumber : number = 0;
  private currentSampleOffset : number = 0;
  constructor() { }

  public processFrame(rawFrame: RawFrame) {
    let audio = rawFrame.audio;
    if (rawFrame.audio !== null) {
      audio = { ...rawFrame.audio, sampleOffset: this.currentSampleOffset };
      this.currentSampleOffset += audio.sampleOffset;
    }

    const rewrittenFrame : RawFrame = {
      ...rawFrame,
      audio,
      frameNumber: this.currentFrameNumber,
    };

    this.currentFrameNumber += 1;

    return rewrittenFrame;
  }
}

export class RawAudioQueue {
  private frame: RawAudioFrame | null = null;

  public push(logger: Logger, audioFrame: RawAudioFrame) {
    if (this.frame === null) {
      const buffer = new Uint8Array(audioFrame.buffer.length);
      audioFrame.buffer.copy(buffer);
      this.frame = { ...audioFrame, buffer: new ReadonlyBuffer([buffer]) };
    } else {
      const expectedSampleOffset = this.frame.sampleOffset + this.frame.sampleCount;
      if (expectedSampleOffset !== audioFrame.sampleOffset) {
        logger.warn(`Audio sample offset discontinuity in RawAudioQueue. ${JSON.stringify({ expectedSampleOffset, sampleOffset: audioFrame.sampleOffset })}`);
      }

      const reason = rawAudioFrameIncompatibilityReason(this.frame, audioFrame);
      if (reason !== null) {
        throw new Error(`Incompatible audio pushed into RawAudioQueue: ${reason}`);
      }
      this.frame.buffer = ReadonlyBuffer.concat([this.frame.buffer, audioFrame.buffer]);
      this.frame.sampleCount += audioFrame.sampleCount;
    }
  }

  public clear() {
    this.frame = null;
  }

  public sampleCount(): number {
    return this.frame?.sampleCount ?? 0;
  }

  public sampelOffset(): number {
    return this.frame?.sampleOffset ?? 0;
  }

  public takeSamples(
    logger: Logger,
    count: number,
    padWithSilence: boolean
  ): RawAudioFrame {
    invariant(this.frame !== null, 'Cannot take samples from audio queue which is not initialized');

    const samplesToTake = Math.min(count, this.frame.sampleCount);
    const bytesPerSample = bytesPerSampleFromRawAudioFormat(this.frame.format);
    const bytesToTake = samplesToTake * this.frame.channelCount * bytesPerSample;

    const buffer = this.frame.buffer.subarray(0, bytesToTake);


    this.frame.buffer = this.frame.buffer.subarray(bytesToTake);

    const frame = {
      ...this.frame,
      sampleCount: samplesToTake,
      buffer,
    };

    this.frame.sampleCount -= samplesToTake;
    this.frame.sampleOffset += samplesToTake;

    if (padWithSilence && (samplesToTake < count)) {
      const samplesToPad = count - samplesToTake;
      const bytesToPad = samplesToPad * frame.channelCount * bytesPerSample;
      const silence = new ReadonlyBuffer([new Uint8Array(bytesToPad)]);

      frame.buffer = ReadonlyBuffer.concat([frame.buffer, silence]);
      frame.sampleCount += samplesToPad;
      logger.warn(`Audio frame incomplete. Padding with ${samplesToPad} samples of silence`);
    }

    return frame;
  }
}

export class SilenceGenerator {
  private inner : ReadonlyBuffer | null = null;

  public silencedRawAudioFrame(audioFrame: RawAudioFrame | null): RawAudioFrame | null {
    if (audioFrame === null) {
      return null;
    }
    return {
      ...audioFrame,
      buffer: this.getSilentBuffer(audioFrame.buffer.length),
    };
  }

  public makeSilenceRawAudioFrame(
    sampleRate: number,
    channelCount: number,
    format: RawAudioFormat,
    frameRate: Fraction,
    frameNumber: number,
  ): RawAudioFrame {
    const sampleOffset = calculateSampleOffsetForFrameNumber(sampleRate, frameRate, frameNumber);
    const sampleCount = calculateSampleCountForFrameNumber(sampleRate, frameRate, frameNumber);
    const bytesPerSample = bytesPerSampleFromRawAudioFormat(format);
    const byteCount = sampleCount * channelCount * bytesPerSample;
    return {
      sampleRate,
      sampleOffset,
      channelCount,
      format,
      sampleCount,
      buffer: this.getSilentBuffer(byteCount),
    };
  }

  public silencedRawFrame(frame: RawFrame): RawFrame {
    return {
      ...frame,
      audio: this.silencedRawAudioFrame(frame.audio),
    };
  }

  private getSilentBuffer(byteCount: number): ReadonlyBuffer {
    if ((this.inner === null) || (this.inner.length < byteCount)) {
      this.inner = new ReadonlyBuffer([new Uint8Array(byteCount)]);
    }
    return this.inner.subarray(0, byteCount);
  }
}


interface QueueEntryFrame {
  type: 'frame',
  rawFrame: RawFrame,
}

interface QueueEntryError {
  type: 'error';
  error: any,
}


type QueueEntry = QueueEntryFrame | QueueEntryError;

export interface RawFrameQueueSettings {
  maxFramesInQueue: number,
}

export class RawFrameQueue {
  private newEntry: MultiGuard = new MultiGuard();
  private entryTaken: MultiGuard = new MultiGuard();
  private entries: QueueEntry[] = [];
  private closed: boolean = false;

  constructor(
    private settings: RawFrameQueueSettings
  ) { }

  public async push(rawFrame: RawFrame): Promise<boolean> {
    if (this.closed) {
      return false;
    }
    while (this.entries.length >= this.settings.maxFramesInQueue) {
      if (this.closed) {
        return false;
      }
      await this.entryTaken.wait();
    }
    this.entries.push({ type: 'frame', rawFrame });
    this.newEntry.notifyAll();
    return true;
  }

  public fail(error: any): boolean {
    if (!this.closed) {
      this.entries.push({ type: 'error', error });
      this.newEntry.notifyAll();
      this.closed = true;
      return true;
    } else {
      return false;
    }
  }

  public close(): boolean {
    if (!this.closed) {
      this.closed = true;
      this.newEntry.notifyAll();
      this.entryTaken.notifyAll();
      return true;
    } else {
      return false;
    }
  }

  public async poll(): Promise<RawFrame | null> {
    while (this.entries.length === 0) {
      if (this.closed) {
        return null;
      }
      await this.newEntry.wait();
    }

    const [nextEntry] = this.entries.splice(0, 1);

    this.entryTaken.notifyAll();

    if (nextEntry.type === 'error') {
      throw nextEntry.error;
    }

    return nextEntry.rawFrame;
  }
}

export const validateRawAudioFormat =
  makeValidationFunction<RawAudioFormat>((u): RawAudioFormat => {
    if (typeof u !== 'string') {
      throw Error('Not a string');
    }

    switch (u) {
      case 'pcm16':
      case 'float32':
        return u;
      default:
        throw new Error('Must be either pcm16 or float32');
    }
  });

export const validateRawDataCodec =
  makeValidationFunction<RawDataCodec>((u): RawDataCodec => {
    if (typeof u !== 'string') {
      throw Error('Not a string');
    }

    switch (u) {
      case 'smpte_436m':
        return u;
      default:
        throw new Error('Must be "smpte_436m"');
    }
  });

export const validateRawVideoInfo =
  makeValidationFunction<RawVideoInfo>((u): RawVideoInfo => {
    return {
      resolution: validateObjectProperty(u, 'resolution', 'Resolution', validateResolution),
      pixelFormat: validateObjectProperty(u, 'pixelFormat', 'PixelFormat', validatePixelFormat),
      fieldOrder: validateObjectProperty(u, 'fieldOrder', 'FieldOrder', validateFieldOrder),
    };
  });

export const validateRawAudioInfo =
  makeValidationFunction<RawAudioInfo>((u): RawAudioInfo => {
    return {
      sampleRate: validateNumberProperty(u, 'sampleRate'),
      format: validateObjectProperty(u, 'format', 'RawAudioFormat', validateRawAudioFormat),
      channelCount: validateNumberProperty(u, 'channelCount'),
    };
  });

export const validateRawDataInfo =
  makeValidationFunction<RawDataInfo>((u): RawDataInfo => {
    return {
      codec: validateObjectProperty(u, 'codec', 'RawDataCodec', validateRawDataCodec),
    }
  });

export const validateRawInfo =
  makeValidationFunction<RawInfo>((u): RawInfo => {
    return {
      frameRate: validateObjectProperty(u, 'frameRate', 'Fraction', validateFraction),
      video: validateObjectOrNullProperty(u, 'video', 'RawVideoInfo', validateRawVideoInfo),
      audio: validateObjectOrNullProperty(u, 'audio', 'RawAudioInfo', validateRawAudioInfo),
      data: validateObjectOrNullProperty(u, 'data', 'RawDataInfo', validateRawDataInfo),
    };

  });

